import type {
  InboxChangesPayload,
  InboxCommentPayload,
  InboxItem,
} from '@store/reducers/inbox'

import { MONTHS } from '@/constants/constants'

const MINUTE = 60_000
const HOUR = 60 * MINUTE
const DAY = 24 * HOUR

// How long ago, in the shortest form that still reads: 5m, 2h, Yesterday, Sep 28
export const relativeTime = (ts: number, now: number): string => {
  const diff = Math.max(0, now - ts)

  if (diff < MINUTE) {
    return 'now'
  }

  if (diff < HOUR) {
    return `${Math.floor(diff / MINUTE)}m`
  }

  if (diff < DAY) {
    return `${Math.floor(diff / HOUR)}h`
  }

  if (diff < 2 * DAY) {
    return 'Yesterday'
  }

  const date = new Date(ts)

  return `${MONTHS[date.getMonth()]} ${date.getDate()}`
}

export interface InboxLine {
  // the first line: where it happened
  title: string
  // the project, shown after the title unless the title is the project
  project: string
  // the second line: who did what
  actor: string
  action: string
  // whose initials the avatar shows when the row has no actor of its own:
  // the first of the people named in `actor`
  avatar: string
}

const isChanges = (
  item: InboxItem
): item is InboxItem & { payload: InboxChangesPayload } =>
  item.kind === 'changes'

const ACTIONS: Record<Exclude<InboxItem['kind'], 'changes'>, string> = {
  comment: 'commented',
  mention: 'mentioned you',
  reply: 'replied',
}

export const describeItem = (item: InboxItem): InboxLine => {
  const project = item.project.title || 'Untitled project'

  if (isChanges(item)) {
    const { actors } = item.payload
    const [first] = actors
    const total = actors.reduce(
      (sum, a) => sum + a.pages + a.content + a.other,
      0
    )
    const others = actors.length - 1
    const avatar = first?.name ?? 'Someone'
    const actor = others > 0 ? `${avatar} and ${others} more` : avatar

    return {
      title: project,
      project: '',
      actor,
      action: `made ${total} change${total === 1 ? '' : 's'}`,
      avatar,
    }
  }

  const payload = item.payload as InboxCommentPayload

  return {
    title: payload.title,
    project,
    actor: payload.author,
    action: ACTIONS[item.kind as keyof typeof ACTIONS],
    avatar: payload.author,
  }
}
